/**
 * The side panel's "Cost figures" tab: every row of the draft's summed tables
 * (blocks with totals), to check against its source and confirm. Confirmed rows
 * are the server's confirmed_rows ("block:row"); totals are computed in the
 * package from these rows. Confirming needs a draft to confirm against, so it
 * opens once the section has one (review, needs input, done or failed).
 */
import { useEffect, useState } from 'react'
import { Check, Info, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { transitApi } from '../../services/transit'
import type { DraftBlock, GraphNode } from '../../types/transit'

/** The draft's summed tables, each with the heading it sits under. */
export function summedTables(blocks: DraftBlock[] = []): Array<{ index: number; heading?: string; block: DraftBlock }> {
  let heading: string | undefined
  const out: Array<{ index: number; heading?: string; block: DraftBlock }> = []
  blocks.forEach((b, index) => {
    if (b.type === 'heading') heading = b.text
    else if (b.type === 'table' && b.totals) out.push({ index, heading, block: b })
  })
  return out
}

/** How many of the section's summed figures there are, and how many are confirmed. */
export function figureCounts(node: GraphNode): { total: number; confirmed: number } {
  const done = new Set(node.confirmed_rows)
  let total = 0
  let confirmed = 0
  for (const t of summedTables(node.data?.blocks)) {
    t.block.rows?.forEach((_, r) => {
      total++
      if (done.has(`${t.index}:${r}`)) confirmed++
    })
  }
  return { total, confirmed }
}

const CONFIRMABLE = ['review', 'needs_input', 'done', 'failed']

export default function CostFigures({ node, onChange }: {
  node: GraphNode
  /** After a row was confirmed or unconfirmed: reload. */
  onChange: () => void
}) {
  const tables = summedTables(node.data?.blocks)
  const done = new Set(node.confirmed_rows)
  const { total, confirmed } = figureCounts(node)
  const canConfirm = CONFIRMABLE.includes(node.status)
  // A row stays spinning until the reloaded node shows the change.
  const [pending, setPending] = useState<Record<string, boolean>>({})
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    setPending((all) => Object.fromEntries(Object.entries(all).filter(([key, want]) => done.has(key) !== want)))
  }, [node.confirmed_rows])

  const toggle = async (block: number, row: number) => {
    const key = `${block}:${row}`
    const want = !done.has(key)
    setPending((all) => ({ ...all, [key]: want }))
    setError(null)
    try {
      await transitApi.confirmRow(node.transition_id, node.node_key, node.revision, block, row, want)
      onChange()
    } catch (e: any) {
      setPending((all) => { const { [key]: _, ...rest } = all; return rest })
      setError(e.message || 'Could not save this figure')
    }
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-medium leading-tight text-foreground">{node.name} figures</h2>
        <p className="text-sm leading-none text-tertiary">{confirmed}/{total} confirmed</p>
      </div>
      {/* The instruction as a callout: the attention yellow of the home page's
          "upload at least one document" note. */}
      <p role="note" className="flex items-center gap-3 rounded-md bg-status-attention/60 px-3 py-2.5 text-sm leading-[1.5] text-status-foreground dark:bg-status-attention">
        <Info className="h-5 w-5 shrink-0" aria-hidden />
        {/* Matches the package (internal/modules/transit/packagedoc): unconfirmed rows are
            listed under the table; a confirmation survives a redraft unless
            the item, amount or group changes (blocks.RowKey). */}
        {canConfirm ? (
          <span className="flex flex-col gap-1">
            <span>Check each figure against its source document and confirm the ones that match.</span>
            <span>
              Can&apos;t verify a figure? Leave it unchecked. It stays in the package, marked as unconfirmed.
            </span>
          </span>
        ) : 'Figures can be confirmed once the section has a draft.'}
      </p>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex shrink-0 flex-col rounded-lg border border-primary p-3">
        <div className="flex flex-col gap-4">
          {tables.map((t, ti) => {
            const cols = t.block.columns ?? []
            const amount = Math.max(0, cols.indexOf(t.block.totals!.column))
            const by = t.block.totals!.by ? cols.indexOf(t.block.totals!.by) : -1
            // Grouped the way the package subtotals them (totals.by), in the
            // order the groups first appear; one group when there's no "by".
            const groups: Array<{ name?: string; rows: number[] }> = []
            t.block.rows?.forEach((r, ri) => {
              const name = by >= 0 ? r[by] : undefined
              const g = groups.find((x) => x.name === name)
              if (g) g.rows.push(ri)
              else groups.push({ name, rows: [ri] })
            })
            const tableDone = t.block.rows?.filter((_, ri) => done.has(`${t.index}:${ri}`)).length ?? 0
            // Laid out like a question (QuestionsPanel): the table's heading as
            // the title, its rows as choice cells, navy with a check when
            // confirmed, and the count where a question's labels go.
            return (
              <div key={t.index} className={cn('flex shrink-0 flex-col gap-4', ti > 0 && 'border-t border-border-subtle pt-4')}>
                {/* Sticks to the top of the list as it scrolls, so the count
                    stays in view. */}
                <div className="sticky top-0 z-10 -mt-1 flex items-start gap-3 bg-card pb-1 pt-1 text-base font-medium leading-[1.5]">
                  <span className="text-tertiary">{ti + 1}</span>
                  <span className="flex-1 text-foreground">{t.heading ?? `Table ${ti + 1}`}</span>
                  <span className="shrink-0 text-sm font-normal text-tertiary">{tableDone}/{t.block.rows?.length ?? 0} confirmed</span>
                </div>
                {groups.map((g) => (
                  <div key={g.name ?? ''} className="flex flex-col gap-1">
                    {/* The group's value from the draft ("Required"), as "Required costs". */}
                    {g.name && <p className="pb-1 text-base font-medium text-tertiary">{g.name} costs</p>}
                    {g.rows.map((ri) => {
                      const r = t.block.rows![ri]
                      const key = `${t.index}:${ri}`
                      const spinning = key in pending
                      const on = spinning ? pending[key] : done.has(key)
                      const note = r.filter((_, k) => k !== 0 && k !== amount && k !== by).filter(Boolean).join(' · ')
                      return (
                        <button key={ri} type="button" aria-pressed={on} disabled={!canConfirm || spinning}
                          onClick={() => toggle(t.index, ri)}
                          className={cn(
                            'flex w-full items-start justify-between gap-3 rounded-md px-4 py-2 text-left text-base leading-[1.5] transition-colors disabled:cursor-default',
                            on ? 'bg-primary text-primary-foreground'
                              : 'bg-foreground/[0.04] text-muted-foreground enabled:hover:bg-secondary-hover enabled:hover:text-foreground dark:bg-foreground/[0.08]'
                          )}>
                          <span className="flex min-w-0 flex-col gap-0.5">
                            <span>{r[0]}</span>
                            {note && <span className="text-sm leading-[1.45] opacity-75">{note}</span>}
                          </span>
                          <span className="flex shrink-0 items-center gap-3">
                            <span className="tabular-nums">{r[amount]}</span>
                            {/* Always shown: an empty box until the figure is confirmed. */}
                            {spinning ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : (
                              <span aria-hidden className={cn(
                                'flex h-4 w-4 items-center justify-center rounded-[4px] border',
                                on ? 'border-primary-foreground bg-primary-foreground text-primary' : 'border-current opacity-60'
                              )}>
                                {on && <Check className="h-3 w-3" strokeWidth={3} />}
                              </span>
                            )}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}
